import ProductAutoComplete from "@/components/Filter/productAutocomplete";
import {
  addSetting,
  copySetting,
  removeSettingByIndex,
  setSettingByIndex,
} from "@/features/commissionSlice";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import _ from "lodash";
import moment from "moment";
import React from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

const MemoTransferProductCommissionsFormRow = React.memo(
  function TransferProductCommissionsFormRow({
    index,
    item,
    validations,
    disabled,
    promoterOptions,
    removeFormData,
    copyFormData,
    productOptions,
    currentDate,
    handleFormData,
  }) {
    return (
      <Stack key={item.id} sx={{ my: 2 }} direction={"row"} spacing={2}>
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems={"flex-start"}
          sx={{ flex: 7 }}
          spacing={1}
        >
          <DatePicker
            sx={{ flex: 1 }}
            disabled={disabled}
            id="date"
            name="date"
            label="日期"
            minDate={moment(currentDate).startOf("month").format("YYYY-MM-DD")}
            maxDate={moment(currentDate).endOf("month").format("YYYY-MM-DD")}
            value={item.date}
            onChange={(value) =>
              handleFormData(
                index,
                "date",
                moment(value).format("YYYY-MM-DD"),
                item,
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                error={!!validations[index]?.dateErr}
                helperText={validations[index]?.dateErr}
              />
            )}
          ></DatePicker>
          <Autocomplete
            sx={{ flex: 1 }}
            required
            disabled={disabled}
            id="combo-box-demo"
            options={promoterOptions}
            value={item.transferUser}
            onChange={(e, newValue) =>
              handleFormData(index, "transferUser", newValue, item)
            }
            label="員工"
            renderInput={(params) => <TextField {...params} label="員工" />}
          />
          <TextField
            disabled={true}
            value={item.rosterString}
            id="outlined-basic"
            label="更表"
            variant="outlined"
          />
          <ProductAutoComplete
            multiple={false}
            disabled={disabled}
            sx={{ width: 250, flex: 7 }}
            productOptions={productOptions}
            handleOnChange={(value) =>
              handleFormData(index, "productId", value, item)
            }
            selectedValue={item.productId ?? []}
            error={!!validations[index]?.productIdErr}
          ></ProductAutoComplete>
          <Box sx={{ flex: 0.5, display: "flex", justifyContent: "center" }}>
            <TextField
              disabled={disabled}
              error={!!validations[index]?.transferQtyErr}
              helperText={validations[index]?.transferQtyErr}
              sx={{ maxWidth: 125, minWidth: 125 }}
              value={item.transferQty}
              type="number"
              id="outlined-basic"
              label="調整數量"
              variant="outlined"
              onChange={(event) =>
                handleFormData(index, "transferQty", event.target.value, item)
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">件</InputAdornment>
                ),
              }}
            />
          </Box>
        </Stack>
        {!disabled && (
          <Stack
            direction={"row"}
            justifyContent="space-between"
            alignItems={"flex-start"}
            sx={{ flex: 1 }}
          >
            <IconButton onClick={() => copyFormData(item, index)}>
              <ContentCopyIcon></ContentCopyIcon>
            </IconButton>
            <IconButton color="error" onClick={() => removeFormData(index)}>
              <DeleteForeverIcon></DeleteForeverIcon>
            </IconButton>
          </Stack>
        )}
      </Stack>
    );
  },
  (prev, next) => {
    return (
      next.index === prev.index &&
      _.isEqual(prev.item, next.item) &&
      _.isEqual(prev.validations, next.validations)
    );
  },
);

export default function TransferProductCommissionsFormRows({
  disabled,
  promoterOptions,
  productOptions,
  handleRoster,
  productCategories,
}) {
  const dispatch = useDispatch();

  const currentDate = useSelector(
    (state) => state.commission.settings.currentDate,
  );

  const formData = useSelector(
    (state) => state.commission.settings.transferProductCommissions,
    shallowEqual,
  );

  const validations = useSelector(
    (state) => state.commission.validations.transferProductCommissions,
    shallowEqual,
  );

  const handleRosterRef = React.useRef(handleRoster);
  handleRosterRef.current = handleRoster;

  const removeFormData = React.useCallback(
    async (index) => {
      dispatch(
        removeSettingByIndex({
          key: "transferProductCommissions",
          index: index,
        }),
      );
    },
    [dispatch],
  );
  const copyFormData = React.useCallback(
    async (item, index) => {
      dispatch(
        copySetting({
          key: "transferProductCommissions",
          index: index,
          value: {
            date: item.date,
            productId: item.productId,
            transferQty: -item.transferQty,
            roster: "FULL",
            rosterString: "",
            key: item + 1,
          },
        }),
      );
    },
    [dispatch],
  );
  const handleFormData = React.useCallback(
    async (index, key, value, currentValue) => {
      let cloneRow = { ...currentValue };
      cloneRow[key] = value;
      if (
        cloneRow["transferUser"] &&
        cloneRow.date &&
        (key === "date" || key === "transferUser")
      ) {
        cloneRow = await handleRosterRef.current(
          cloneRow,
          cloneRow["transferUser"].value,
          cloneRow.date,
        );
      }
      dispatch(
        setSettingByIndex({
          key: "transferProductCommissions",
          value: cloneRow,
          index: index,
        }),
      );
    },
    [dispatch],
  );
  const createFormData = React.useCallback(() => {
    dispatch(
      addSetting({
        key: "transferProductCommissions",
        value: {
          date: moment(currentDate).format("YYYY-MM-DD"),
          transferQty: null,
          roster: "FULL",
          rosterString: "",
        },
      }),
    );
  }, [dispatch, currentDate]);
  return (
    <React.Fragment>
      {_.map(formData, (item, index) => (
        <MemoTransferProductCommissionsFormRow
          index={index}
          item={item}
          key={item.id}
          validations={validations}
          productCategories={productCategories}
          disabled={disabled}
          promoterOptions={promoterOptions}
          removeFormData={removeFormData}
          copyFormData={copyFormData}
          productOptions={productOptions}
          currentDate={currentDate}
          handleFormData={handleFormData}
        ></MemoTransferProductCommissionsFormRow>
      ))}
      {!disabled && (
        <Box>
          <IconButton component="label" onClick={createFormData}>
            <AddBoxOutlinedIcon />
          </IconButton>
        </Box>
      )}
    </React.Fragment>
  );
}
